import React from 'react';
import { Button, Spinner } from 'src/components/atoms';
import { StyledIconInButton } from 'src/components/foundations/foundations.styles';
import {
  CaseScreenBackButton,
  Modal,
  PageHeaderWithActions,
} from 'src/components/molecules';
import {
  ActionContainer,
  DraftHeaderMain,
  DraftHeaderRow,
} from './DraftCaseHeader.styles';

interface DraftCaseHeaderProps {
  isExistingReport: boolean;
  onBack: () => void;
  onSaveDraft: () => void;
  onDelete: () => void;
  onAuditLog?: () => void;
  saveButtonDisabled?: boolean;
  saveButtonLoading?: boolean;
}

export const DraftCaseHeader = ({
  isExistingReport,
  onBack,
  onSaveDraft,
  onDelete,
  onAuditLog,
  saveButtonDisabled = false,
  saveButtonLoading = false,
}: DraftCaseHeaderProps) => {
  const [showDeleteConfirmation, setShowDeleteConfirmation] =
    React.useState(false);
  const [deleteLoading, setDeleteLoading] = React.useState(false);
  const header = isExistingReport ? 'Add existing case' : 'Add new case';
  const subtitle =
    'Liaise with reporter and enter the following details to manually submit on their behalf';

  return (
    <>
      <DraftHeaderRow>
        <CaseScreenBackButton
          onClick={onBack}
          data-testid="draft-case-back-button"
        />
        <DraftHeaderMain>
          <PageHeaderWithActions
            title={header}
            description={subtitle}
            $minHeight="auto"
            customActions={
              <ActionContainer>
                <Button
                  variant="secondary"
                  data-testid="delete-draft-button"
                  onClick={() => setShowDeleteConfirmation(true)}
                >
                  <StyledIconInButton
                    size="medium"
                    position="left"
                    className="uil uil-trash-alt"
                  />
                  Delete
                </Button>
                <Button
                  variant="secondary"
                  data-testid="save-draft-button"
                  onClick={onSaveDraft}
                  disabled={saveButtonDisabled}
                  loading={saveButtonLoading}
                >
                  {!saveButtonLoading && (
                    <StyledIconInButton
                      size="medium"
                      position="left"
                      className="uil uil-save"
                    />
                  )}
                  {saveButtonLoading && (
                    <Spinner
                      size="small"
                      variant="tertiary"
                      data-testid="save-draft-spinner"
                    />
                  )}
                  Save draft
                </Button>
                <Button
                  variant="secondary"
                  data-testid="audit-log-button"
                  disabled={!onAuditLog}
                  onClick={() => onAuditLog?.()}
                >
                  Audit log
                </Button>
              </ActionContainer>
            }
          />
        </DraftHeaderMain>
      </DraftHeaderRow>
      <Modal
        open={showDeleteConfirmation}
        setOpen={setShowDeleteConfirmation}
        size="medium"
        title="Delete case?"
        description="Are you sure you want to delete this report? You will not be able to recover the report after deletion."
        confirmButtonText="Delete case"
        cancelButtonText="Keep case"
        loading={deleteLoading}
        onCancel={() => setShowDeleteConfirmation(false)}
        onConfirm={() => {
          setDeleteLoading(true);
          onDelete();
        }}
        data-testid="delete-confirmation-modal"
      />
    </>
  );
};
